<template>
  <my-giving-layout :enable-scroll-event="true">
    <div class="hogc-theme-campaign bg-campaign-surface w-full min-h-full flex flex-col">
      <!-- D's header: the banner, with its back to MyGiving, for a pledge already made. -->
      <PledgeHeader :back="backToMyGiving" banner small
        >You are a part<br />of history!</PledgeHeader
      >
      <div class="px-[16px] md:px-[24px] lg:px-[28px] pt-[24px] pb-[24px]">
        <div class="w-full md:max-w-2xl md:mx-auto flex flex-col gap-[20px]">
          <!-- The pledge as D1–D5 draw it, with no status line: members never see a
               pending or Invalid state (Accounts checks couples and youth approval
               behind the scenes), and a spouse's verified couple pledge reads the same,
               the couple card naming the spouse who submitted it. -->
          <!-- A youth saw their pledge on D; once they have left it, it is kept from
               them and they are told so (frame J2-App-Youth-NoAccess). -->
          <DsCard
            v-if="pledge && hideDetails"
            class="flex flex-col items-center gap-[14px] text-center px-[24px] py-[32px]"
            data-test="bf-view-youth-locked"
          >
            <span
              class="w-[64px] h-[64px] rounded-full bg-campaign-highlight flex items-center justify-center text-campaign-ink"
              aria-hidden="true"
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="4" y="10" width="16" height="11" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </span>
            <DsHeading tag="h2" class="text-[22px] leading-[28px] font-black text-campaign-ink">
              Your pledge is in, but you can't view it here
            </DsHeading>
            <DsBody class="text-text-body-md text-campaign-muted">
              Pledges from members in Secondary School or aged 16 and below aren't shown after
              they're submitted.
            </DsBody>
            <DsBody class="text-text-body-md text-campaign-muted">
              If you need to check or change your pledge, please contact Accounts.
            </DsBody>
          </DsCard>
          <PledgeSummary v-else-if="pledge" :summary="summary" />

          <div v-else-if="loadFailed" class="flex flex-col gap-[16px]" data-test="bf-view-error">
            <DsBody class="text-text-body-md text-campaign-ink"
              >We couldn't load your pledge.</DsBody
            >
            <DsButton
              variant="campaign"
              size="52"
              class="w-full"
              data-test="bf-view-retry"
              @click="load"
            >
              Try again
            </DsButton>
          </div>

          <Loading v-else data-test="bf-view-loading" />
        </div>
      </div>
      <!-- The youth frame has no Dreams & Prayers: Give now and back instead. -->
      <div
        v-if="pledge && hideDetails"
        class="px-[16px] md:px-[24px] lg:px-[28px] pb-[32px] w-full md:max-w-2xl md:mx-auto flex flex-col gap-[4px]"
      >
        <DsButton
          variant="campaign"
          size="52"
          class="w-full"
          data-test="bf-view-youth-give-now"
          @click="giveNow"
        >
          Give now
        </DsButton>
        <!-- A plain text action in the frame, not a filled button. -->
        <button
          type="button"
          class="h-[48px] w-full border-0 bg-transparent text-[16px] font-bold text-campaign-primary"
          data-test="bf-view-youth-back"
          @click="backToMyGiving"
        >
          Back to MyGiving
        </button>
      </div>
      <!-- Give now, then Back to MyGiving as a plain text action, as V draws them. -->
      <DreamsPrayers v-else>
        <div class="flex flex-col gap-[8px]">
          <DsButton
            variant="campaign"
            size="52"
            class="w-full"
            data-test="bf-view-give-now"
            @click="giveNow"
          >
            Give now
          </DsButton>
          <button
            type="button"
            class="h-[48px] w-full border-0 bg-transparent text-[16px] font-bold text-campaign-primary"
            data-test="bf-view-back"
            @click="backToMyGiving"
          >
            Back to MyGiving
          </button>
        </div>
      </DreamsPrayers>
    </div>
  </my-giving-layout>
</template>

<script>
import { mapState } from 'vuex';
import {
  Body as DsBody,
  Button as DsButton,
  Card as DsCard,
  Heading as DsHeading,
} from '@team-hogc/hogc-one-design-system';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import Loading from '@/components/common/LoadingState.vue';
import DreamsPrayers from '@/components/MyGiving/BuildingFundPledge/DreamsPrayers.vue';
import PledgeSummary from '@/components/MyGiving/BuildingFundPledge/PledgeSummary.vue';
import PledgeHeader from '@/components/MyGiving/BuildingFundPledge/PledgeHeader.vue';
import { BF_PLEDGE_ROUTES } from '@/constants/MyGiving/building-fund-pledge';
import { bfGiveNowRoute, hidesPledgeFromYouth, pledgeSummary } from '@/utils/building-fund-pledge';

// V of the BF2026 digital pledge: the member's pledge for the campaign, read-only,
// from GET /pledges/confirm, with the done pages' cards, then Give now. Reached from
// the landing banner, from the form once the member has pledged, and from /done on
// a reload.
export default {
  name: 'BuildingFundPledgeView',

  components: {
    MyGivingLayout,
    DreamsPrayers,
    Loading,
    PledgeSummary,
    DsBody,
    DsButton,
    DsCard,
    DsHeading,
    PledgeHeader,
  },

  data() {
    return {
      loadFailed: false,
    };
  },

  computed: {
    ...mapState('buildingFundPledge', ['pledge']),

    // Read live from the store rather than copied on entry: Ionic keeps this page
    // alive, and a refresh on re-entry should show at once.
    summary() {
      return pledgeSummary(this.pledge);
    },

    hideDetails() {
      return hidesPledgeFromYouth(this.pledge, this.$store.state.profile);
    },
  },

  ionViewWillEnter() {
    this.load();
  },

  methods: {
    // Refetch on every entry. `null` means giving-api has no pledge for the year,
    // so there is nothing to view; `undefined` means the call failed and the store
    // has toasted — stay, and offer a retry rather than bouncing the member.
    async load() {
      this.loadFailed = false;
      const pledge = await this.$store.dispatch('buildingFundPledge/fetchPledge');
      if (pledge === null) {
        this.$router.replace(BF_PLEDGE_ROUTES.form);
        return;
      }
      this.loadFailed = pledge === undefined;
    },

    backToMyGiving() {
      this.$router.replace('/my-giving');
    },

    // Give now with Building Fund preselected, as the BF2026 QR opens it. No amount:
    // only the done page, straight after a one-time pledge, fills one in.
    giveNow() {
      this.$router.replace(bfGiveNowRoute());
    },
  },
};
</script>
